import * as React from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useLazyGetApiProgramsDeliverableTypesQuery } from '../../../../app/athenaApiGen';

export default function Review() {
  const {
    formData,
    modalMode,
    attachmentUploadStatus: uploadStatus,
    existingAttachments,
    deletedAttachmentIds,
    originalDeliverableTypeIds,
    originalDeliverableTypes,
  } = useSelector((state) => state.projects);
  const attachmentNames = formData.attachmentNames || [];
  const selectedDeliverableTypeIds = formData.selectedDeliverableTypes || [];
  const programId = formData.programId;

  // Resolve display names against the parent program's deliverable-type list.
  // Uses the same lazy hook + arg as DeliverableTypesForm so RTK Query serves
  // this subscriber from cache without a duplicate network call.
  const [fetchDeliverableTypes, { data: programDeliverableTypes }] =
    useLazyGetApiProgramsDeliverableTypesQuery();

  React.useEffect(() => {
    if (programId && selectedDeliverableTypeIds.length > 0) {
      fetchDeliverableTypes({ programId });
    }
  }, [programId, selectedDeliverableTypeIds.length, fetchDeliverableTypes]);
  
  // Filter to get only selected deliverable types and deduplicate.
  // Falls back to originalDeliverableTypes for orphans (selections whose
  // deliverableTypeId is no longer in the program's current list — edit mode).
  const selectedDeliverableTypes = React.useMemo(() => {
    if (selectedDeliverableTypeIds.length === 0) return [];

    const uniqueMap = new Map();
    if (Array.isArray(programDeliverableTypes)) {
      programDeliverableTypes.forEach(dt => {
        if (selectedDeliverableTypeIds.includes(dt.deliverableTypeId) && !uniqueMap.has(dt.deliverableTypeId)) {
          uniqueMap.set(dt.deliverableTypeId, dt);
        }
      });
    }

    if (Array.isArray(originalDeliverableTypes)) {
      originalDeliverableTypes.forEach(dt => {
        if (
          selectedDeliverableTypeIds.includes(dt.deliverableTypeId) &&
          !uniqueMap.has(dt.deliverableTypeId)
        ) {
          uniqueMap.set(dt.deliverableTypeId, dt);
        }
      });
    }

    return Array.from(uniqueMap.values());
  }, [programDeliverableTypes, originalDeliverableTypes, selectedDeliverableTypeIds]);

  // Calculate added, kept, and removed deliverable types for edit mode
  const { addedTypes, keptTypes, removedTypes } = React.useMemo(() => {
    if (modalMode !== 'edit') {
      return { addedTypes: [], keptTypes: [], removedTypes: [] };
    }

    const originalIds = originalDeliverableTypeIds || [];
    const currentIds = selectedDeliverableTypeIds || [];

    // Added: in current but not in original
    const addedIds = currentIds.filter(id => !originalIds.includes(id));
    // Kept: in both current and original
    const keptIds = currentIds.filter(id => originalIds.includes(id));
    // Removed: in original but not in current
    const removedIds = originalIds.filter(id => !currentIds.includes(id));

    // Get full type objects for display. Prefer the program-scoped list,
    // fall back to originalDeliverableTypes (covers orphans + removed items).
    const getTypeById = (id) => {
      if (programDeliverableTypes) {
        const found = programDeliverableTypes.find(dt => dt.deliverableTypeId === id);
        if (found) return found;
      }
      if (originalDeliverableTypes) {
        const found = originalDeliverableTypes.find(dt => dt.deliverableTypeId === id);
        if (found) return found;
      }
      return null;
    };

    return {
      addedTypes: addedIds.map(getTypeById).filter(Boolean),
      keptTypes: keptIds.map(getTypeById).filter(Boolean),
      removedTypes: removedIds.map(getTypeById).filter(Boolean),
    };
  }, [modalMode, originalDeliverableTypeIds, selectedDeliverableTypeIds, programDeliverableTypes, originalDeliverableTypes]);
  
  const getStatusIcon = React.useCallback((filename) => {
    const status = uploadStatus.find(s => s.filename === filename);
    if (!status) return <InsertDriveFileIcon sx={{ color: 'text.secondary', fontSize: 24 }} />;
    
    switch (status.status) {
      case 'pending':
        return <HourglassEmptyIcon sx={{ color: 'text.secondary', fontSize: 24 }} />;
      case 'uploading':
        return <CircularProgress size={20} sx={{ color: 'info.main' }} />;
      case 'success':
        return <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 24 }} />;
      case 'error':
        return <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 24 }} />;
      default:
        return <InsertDriveFileIcon sx={{ color: 'text.secondary', fontSize: 24 }} />;
    }
  }, [uploadStatus]);
  
  const getStatusText = React.useCallback((filename) => {
    const status = uploadStatus.find(s => s.filename === filename);
    if (!status) return null;
    
    switch (status.status) {
      case 'pending':
        return <Typography variant="caption" sx={{ color: 'text.secondary' }}>Pending</Typography>;
      case 'uploading':
        return <Typography variant="caption" sx={{ color: 'info.main' }}>Uploading...</Typography>;
      case 'success':
        return <Typography variant="caption" sx={{ color: 'success.main' }}>Uploaded</Typography>;
      case 'error':
        return <Typography variant="caption" sx={{ color: 'error.main' }}>{status.error || 'Failed'}</Typography>;
      default:
        return null;
    }
  }, [uploadStatus]);
  
  // Check if any files are currently uploading
  const isUploading = uploadStatus.some(s => s.status === 'uploading');
  const uploadedCount = uploadStatus.filter(s => s.status === 'success').length;
  const totalCount = uploadStatus.length;

  return (
    <Stack spacing={2} sx={{ overflow: 'hidden' }}>
      {/* Success Message or Upload Progress */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1,
          backgroundColor: isUploading ? 'info.lighter' : 'success.lighter',
          borderRadius: 1,
          border: 1,
          borderColor: isUploading ? 'info.light' : 'success.light',
        }}
      >
        {isUploading ? (
          <CircularProgress size={32} sx={{ color: 'info.main' }} />
        ) : (
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />
        )}
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isUploading ? 'info.dark' : 'success.dark' }}>
            {isUploading ? 'Uploading Attachments...' : (modalMode === 'edit' ? 'Ready to Update' : 'Ready to Submit')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {isUploading 
              ? `Uploading ${uploadedCount} of ${totalCount} files...`
              : `Please review the information below before ${modalMode === 'edit' ? 'updating' : 'creating'} the project.`
            }
          </Typography>
        </Box>
      </Box>

      {/* Deliverable Types */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Deliverable Types ({selectedDeliverableTypes.length})
        </Typography>
        {(selectedDeliverableTypes.length > 0 || removedTypes.length > 0) ? (
          <Box sx={{ maxHeight: 190, overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
            <Stack spacing={1.5}>

              {/* In Edit Mode: Show Kept Types (existing, unchanged) */}
              {modalMode === 'edit' && keptTypes.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                    Existing Deliverable Types ({keptTypes.length})
                  </Typography>
                  <Stack spacing={1}>
                    {keptTypes.map((dt) => (
                      <Box
                        key={dt.deliverableTypeId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1.5,
                          py: 0.75,
                          border: 1,
                          borderColor: 'success.light',
                          borderRadius: 1,
                          backgroundColor: 'success.lighter',
                        }}
                      >
                        <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 24 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Del #{dt.deliverableNumber} - {dt.deliverableType?.title || 'Untitled'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'success.dark' }}>
                            Already assigned
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* In Edit Mode: Show Added Types (newly selected) */}
              {modalMode === 'edit' && addedTypes.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 600, mb: 0.5, display: 'block' }}>
                    New Deliverable Types ({addedTypes.length})
                  </Typography>
                  <Stack spacing={1}>
                    {addedTypes.map((dt) => (
                      <Box
                        key={dt.deliverableTypeId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1.5,
                          py: 0.75,
                          border: 1,
                          borderColor: 'info.light',
                          borderRadius: 1,
                          backgroundColor: 'info.lighter',
                        }}
                      >
                        <AddCircleOutlineIcon sx={{ color: 'info.main', fontSize: 24 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Del #{dt.deliverableNumber} - {dt.deliverableType?.title || 'Untitled'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'info.dark' }}>
                            Will be added
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* In Edit Mode: Show Removed Types */}
              {modalMode === 'edit' && removedTypes.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, mb: 0.5, display: 'block' }}>
                    Removed Deliverable Types ({removedTypes.length})
                  </Typography>
                  <Stack spacing={1}>
                    {removedTypes.map((dt) => (
                      <Box
                        key={dt.deliverableTypeId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1.5,
                          py: 0.75,
                          border: 1,
                          borderColor: 'error.light',
                          borderRadius: 1,
                          backgroundColor: 'error.lighter',
                          opacity: 0.8,
                        }}
                      >
                        <RemoveCircleOutlineIcon sx={{ color: 'error.main', fontSize: 24 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textDecoration: 'line-through',
                            }}
                          >
                            Del #{dt.deliverableNumber} - {dt.deliverableType?.title || 'Untitled'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'error.dark' }}>
                            Will be removed
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* In Create Mode: Show all selected types */}
              {modalMode !== 'edit' && selectedDeliverableTypes.map((dt) => (
                <Box
                  key={dt.deliverableTypeId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 0.75,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <AssignmentIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Del #{dt.deliverableNumber} - {dt.deliverableType?.title || 'Untitled'}
                    </Typography>
                    {dt.deliverableType?.abbreviation && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {dt.deliverableType.abbreviation}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}

            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No deliverable types selected
          </Typography>
        )}
      </Box>

      {/* Attachments */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Attachments ({(existingAttachments?.length || 0) + attachmentNames.length})
        </Typography>
        {(existingAttachments?.length > 0 || attachmentNames.length > 0 || deletedAttachmentIds?.length > 0) ? (
          <Box sx={{ maxHeight: 190, overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
            <Stack spacing={1.5}>
              
              {/* Existing Attachments */}
              {existingAttachments && existingAttachments.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                    Existing Attachments ({existingAttachments.length})
                  </Typography>
                  <Stack spacing={1}>
                    {existingAttachments.map((attachment) => (
                      <Box
                        key={attachment.attachmentId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1.5,
                          py: 0.75,
                          border: 1,
                          borderColor: 'success.light',
                          borderRadius: 1,
                          backgroundColor: 'success.lighter',
                        }}
                      >
                        <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 24 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {attachment.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'success.dark' }}>
                            Already attached
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* New Attachments to Upload */}
              {attachmentNames.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                    New Attachments ({attachmentNames.length})
                  </Typography>
                  <Stack spacing={1}>
                    {attachmentNames.map((fileName, index) => (
                      <Box
                        key={`${fileName}-${index}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1.5,
                          py: 0.75,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          backgroundColor: 'background.paper',
                        }}
                      >
                        {getStatusIcon(fileName)}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {fileName}
                          </Typography>
                          {getStatusText(fileName)}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Deleted Attachments */}
              {deletedAttachmentIds && deletedAttachmentIds.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, mb: 0.5, display: 'block' }}>
                    Deleted Attachments ({deletedAttachmentIds.length})
                  </Typography>
                  <Stack spacing={1}>
                    {deletedAttachmentIds.map((attachment) => (
                      <Box
                        key={attachment.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1.5,
                          py: 0.75,
                          border: 1,
                          borderColor: 'error.light',
                          borderRadius: 1,
                          backgroundColor: 'error.lighter',
                          textDecoration: 'line-through',
                          opacity: 0.7,
                        }}
                      >
                        <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 24 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {attachment.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'error.dark' }}>
                            Will be deleted
                          </Typography>
                        </Box>
                      </Box>
                  ))}
                  </Stack>
                </Box>
              )}

            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No files attached
          </Typography>
        )}
      </Box>
    </Stack>
  );
}